import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "../styles/album-import.css";

/**
 * アルバムQRインポート（認証不要）
 * タブレットの QR から session / secret を渡し、スマホから写真を一時アップロードする。
 */
function apiPath(path: string): string {
  if (typeof window !== "undefined" && window.location.hostname === "localhost") {
    return `https://admin.caretablet.jp${path}`;
  }
  return path;
}

/** Cloud Functions の JSON ボディ制限に合わせた上限（サーバーと同じ・写真・動画共通） */
const MAX_FILE_BYTES = 20 * 1024 * 1024;
/** 1 QR あたりの最大枚数（サーバーと同じ） */
const MAX_FILES_PER_BATCH = 20;

type ProgressState =
  | { phase: "idle" }
  | { phase: "reading"; current: number; total: number }
  | { phase: "uploading"; current: number; total: number }
  | { phase: "completing"; current: number; total: number };

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = reader.result;
      if (typeof r !== "string") {
        reject(new Error("ファイルの読み込みに失敗しました"));
        return;
      }
      const comma = r.indexOf(",");
      resolve(comma >= 0 ? r.slice(comma + 1) : r);
    };
    reader.onerror = () => reject(reader.error ?? new Error("ファイルの読み込みに失敗しました"));
    reader.readAsDataURL(file);
  });
}

const VIEWPORT_LOCK_CLASS = "album-import-viewport-lock";

const AlbumImportPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const session = searchParams.get("session") || "";
  const secret = searchParams.get("secret") || "";

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    html.classList.add(VIEWPORT_LOCK_CLASS);
    body.classList.add(VIEWPORT_LOCK_CLASS);
    root?.classList.add(VIEWPORT_LOCK_CLASS);

    const resetScroll = () => {
      window.scrollTo(0, 0);
      html.scrollLeft = 0;
      body.scrollLeft = 0;
      if (root) root.scrollLeft = 0;
    };
    resetScroll();
    requestAnimationFrame(resetScroll);

    return () => {
      html.classList.remove(VIEWPORT_LOCK_CLASS);
      body.classList.remove(VIEWPORT_LOCK_CLASS);
      root?.classList.remove(VIEWPORT_LOCK_CLASS);
    };
  }, []);

  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<ProgressState>({ phase: "idle" });
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const validParams = useMemo(() => session.length > 0 && secret.length > 0, [session, secret]);

  const isAllowedMediaFile = useCallback((f: File) => {
    if (f.type.startsWith("image/") || f.type.startsWith("video/")) return true;
    const n = f.name.toLowerCase();
    return /\.(jpe?g|png|gif|webp|bmp|mp4|mov|m4v|webm|mkv|3gp|avi)$/i.test(n);
  }, []);

  const onPick = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    if (!list?.length) return;
    const picked = Array.from(list).filter(isAllowedMediaFile);
    if (picked.length === 0) {
      setError("写真または動画のファイルを選んでください");
      return;
    }
    const merged = [...files, ...picked].slice(0, MAX_FILES_PER_BATCH);
    setFiles(merged);
    setError(null);
    setMessage(null);
    e.target.value = "";
  }, [files, isAllowedMediaFile]);

  const removeAt = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearAll = useCallback(() => {
    setFiles([]);
    setError(null);
    setMessage(null);
  }, []);

  const upload = useCallback(async () => {
    if (files.length === 0 || !validParams) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    const total = files.length;
    let notifiedAny = false;
    let notifiedAll = true;

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const n = i + 1;

        if (file.size > MAX_FILE_BYTES) {
          throw new Error(
            `「${file.name}」は ${MAX_FILE_BYTES / (1024 * 1024)}MB 以下にしてください（${n}/${total} 件目）`
          );
        }

        setProgress({ phase: "reading", current: n, total });
        const dataBase64 = await readFileAsBase64(file);

        setProgress({ phase: "uploading", current: n, total });
        const upRes = await fetch(apiPath("/api/uploadAlbumImportFile"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionId: session,
            secret,
            fileName: file.name,
            contentType: file.type || "application/octet-stream",
            dataBase64,
          }),
        });
        const upJson = await upRes.json().catch(() => ({}));
        if (!upRes.ok) {
          throw new Error(
            (upJson as { error?: string }).error || `アップロードに失敗しました（${n}/${total} 件目）`
          );
        }
        const storagePath = (upJson as { storagePath?: string }).storagePath;
        const contentType =
          (upJson as { contentType?: string }).contentType || file.type || "application/octet-stream";
        if (!storagePath) {
          throw new Error(`サーバー応答が不正です（${n}/${total} 件目）`);
        }

        setProgress({ phase: "completing", current: n, total });
        const doneRes = await fetch(apiPath("/api/completeAlbumImportUpload"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionId: session,
            secret,
            storagePath,
            originalFileName: file.name,
            contentType,
          }),
        });
        const doneJson = await doneRes.json().catch(() => ({}));
        if (!doneRes.ok) {
          throw new Error(
            (doneJson as { error?: string }).error || `完了通知に失敗しました（${n}/${total} 件目）`
          );
        }
        if ((doneJson as { notified?: boolean }).notified) {
          notifiedAny = true;
        } else {
          notifiedAll = false;
        }
      }

      setFiles([]);
      setMessage(
        notifiedAll && notifiedAny
          ? `${total} 件の送信が完了しました。タブレットに届きます。`
          : notifiedAny
            ? `${total} 件の保存は完了しました。タブレットへの通知の一部に失敗した可能性があります。`
            : `${total} 件の保存は完了しましたが、タブレットへの通知に失敗した可能性があります。`
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setProgress({ phase: "idle" });
      setBusy(false);
    }
  }, [files, secret, session, validParams]);

  const progressLabel = useMemo(() => {
    if (progress.phase === "idle") return "";
    const { current, total } = progress;
    if (progress.phase === "reading") return `${total} 件中 ${current} 件目を読み込み中…`;
    if (progress.phase === "uploading") return `${total} 件中 ${current} 件目をアップロード中…`;
    return `${total} 件中 ${current} 件目を確定中…`;
  }, [progress]);

  if (!validParams) {
    return (
      <div className="album-import-wrap">
        <div className="album-import-card">
          <h1>写真のインポート</h1>
          <p className="album-import-muted">
            QRコードから再度アクセスするか、タブレットで「写真をインポート」を開き直してください。
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="album-import-wrap">
      {busy && (
        <div className="album-import-overlay" role="status" aria-live="polite">
          <div className="album-import-overlay-card">
            <div className="album-import-hourglass" aria-hidden />
            <p className="album-import-overlay-title">送信中</p>
            <p className="album-import-overlay-progress">{progressLabel || "準備中…"}</p>
          </div>
        </div>
      )}

      <div className="album-import-card">
        <h1>写真をタブレットへ送る</h1>
        <p className="album-import-intro">写真をCare Tabletに送付します。</p>
        <ul className="album-import-notes">
          <li>・複数の写真、動画を一括送付できます（最大{MAX_FILES_PER_BATCH}枚）</li>
          <li>・1枚当たりの最大サイズは{MAX_FILE_BYTES / (1024 * 1024)}MBです。</li>
          <li>・タブレットが取り込むとアップロードされた写真は削除されます。</li>
          <li>・失敗しても一定時間後にファイルは削除され、ファイルがサーバーに残ることはありません。</li>
        </ul>

        <label className="album-import-file-label">
          <span className="album-import-btn-secondary">写真・動画を追加</span>
          <input type="file" accept="image/*,video/*" multiple onChange={onPick} disabled={busy} />
        </label>

        {files.length > 0 && (
          <div className="album-import-list-wrap">
            <div className="album-import-list-header">
              <span>選択中 {files.length} 件</span>
              <button type="button" className="album-import-link-btn" onClick={clearAll} disabled={busy}>
                すべてクリア
              </button>
            </div>
            <ul className="album-import-file-list">
              {files.map((f, idx) => (
                <li key={`${f.name}-${f.size}-${idx}`} className="album-import-file-row">
                  <span className="album-import-file-meta">
                    <span className="album-import-file-name">{f.name}</span>
                    <span className="album-import-file-size">{(f.size / 1024 / 1024).toFixed(2)} MB</span>
                  </span>
                  <button
                    type="button"
                    className="album-import-remove"
                    onClick={() => removeAt(idx)}
                    disabled={busy}
                    aria-label={`${f.name} を削除`}
                  >
                    削除
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <button
          type="button"
          className="album-import-btn-primary"
          disabled={files.length === 0 || busy}
          onClick={() => void upload()}
        >
          {busy ? "送信中…" : `タブレットへ送信（${files.length} 件）`}
        </button>

        {message && <p className="album-import-success">{message}</p>}
        {error && <p className="album-import-error">{error}</p>}
      </div>
    </div>
  );
};

export default AlbumImportPage;
